import React, { useContext, useEffect, useState } from 'react';
import { Row, notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import CreateUpdateAddonForm from '../../components/CreateUpdateAddonForm/CreateUpdateAddonForm';

function AddonCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHeaderComponent } = useContext(LayoutContext);
  const { doFetch: createAddonFetch } = useFetch();
  const { doFetch: uploadImageFetch } = useFetch();
  const { doFetch: fetchLanguages } = useFetch();

  const [languageList, setLanguageList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const languagesData = await new Promise((resolve, reject) => {
          fetchLanguages({
            url: URLS.languages,
            onSuccess: resolve,
            onError: reject,
          });
        });
        setLanguageList(languagesData);
        setHeaderComponent('Create addon');
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
    return () => setHeaderComponent(null);
  }, [fetchLanguages]);

  const uploadImage = async (addonData, formValues) => {
    if (!formValues?.image?.file?.originFileObj) {
      notification.success({ placement: 'bottomRight', message: 'Addon created.', duration: 3 });
      queryClient.invalidateQueries({ queryKey: ['addons'] });
      navigate('/addons');
      return;
    }
    await uploadImageFetch({
      method: 'FILE_POST',
      url: URLS.uploadAddonImage({ addonId: addonData.id }),
      params: { image: formValues.image.file.originFileObj },
      showSuccessNotification: true,
      successMessage: 'Addon created.',
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['addons'] });
        navigate('/addons');
      },
    });
  };

  const onSubmit = async formValues => {
    const translationsObject = languageList.reduce((acc, language) => {
      acc[language.id] = formValues[`name.${language.id}`];
      return acc;
    }, {});

    await createAddonFetch({
      url: URLS.createAddon(),
      params: {
        ...translationsObject,
        price: formValues.price,
        price_wholesale: formValues.priceWholesale,
        currency: formValues.currency,
        is_in_stock: formValues.isInStock,
      },
      onSuccess: data => uploadImage(data, formValues),
    });
  };

  if (isLoading) {
    return (
      <Row>
        <div style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>
      </Row>
    );
  }

  return (
    <Row>
      <CreateUpdateAddonForm languageList={languageList} onSubmit={onSubmit} />
    </Row>
  );
}

export default AddonCreate;
